import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompanyAccount } from '../queryKeys';

import { fetchCompanyAccountSupplierLetterOverrides } from './fetchCompanyAccountSupplierLetterOverrides';
import { type SupplierLetterOverridesResponse } from './types';

/**
 * Generates the query options for the company account supplier letter overrides query.
 *
 * @param companyAccountId - The ID of the company account.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_account_supplier_letter_overrides_vadmin_company_accounts__company_account_id__supplier_letter_overrides_get
 */
// fallow-ignore-next-line unused-export
export const getCompanyAccountSupplierLetterOverridesQueryOptions = <
  TData = SupplierLetterOverridesResponse | null,
>({
  companyAccountId,
  options,
}: {
  companyAccountId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<SupplierLetterOverridesResponse | null, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: companyAccountId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCompanyAccountSupplierLetterOverrides(companyAccountId, {
            signal,
          })
      : skipToken,
    queryKey: queryKeysCompanyAccount.getSupplierLetterOverrides(
      companyAccountId ?? '',
    ),
    staleTime: ms('1h'),
    ...options,
  });

/**
 * React Query based custom hook for getting the list of child companies that have their own
 * supplier letter override, for a given CompanyAccount.
 * Handles 404 errors silently as this is an expected state.
 *
 * @param companyAccountId - The ID of the company account.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_account_supplier_letter_overrides_vadmin_company_accounts__company_account_id__supplier_letter_overrides_get
 */
export const useQueryCompanyAccountSupplierLetterOverrides = <
  TData = SupplierLetterOverridesResponse | null,
>(
  companyAccountId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SupplierLetterOverridesResponse | null, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getCompanyAccountSupplierLetterOverridesQueryOptions<TData>({
      companyAccountId,
      options,
    }),
  );
